import React, { useState, useContext, useEffect } from "react";
import { Navigate } from "react-router-dom";
import { Button } from "@mantine/core";
import { AuthContext } from "../../../providers/AuthProvider";
import DirectoryTable from "../../../themes/components/DirectoryTable";
import { dateFormat } from "../../../utils/DateTime";
import MatchPayslipModal from "../elements/MatchPayslipModal";
import useEmployeeListSearchApiService from "../../employees/services/api/EmployeeListSearchApiService";
import { DocumentProcessingJobStatus } from "../services/model/DocumentProcessingJob";
import DocumentImporterConfiguration from "../services/model/DocumentImporterConfiguration";
import UnmatchedDocumentsResult from "../services/model/UnmatchedDocumentsResult";
import UnmatchedDocument from "../services/model/UnmatchedDocument";
import { AnalyticsContext } from "../../../providers/AnalyticsProvider";

type PayslipIssuesTableProps = {
  data: UnmatchedDocumentsResult;
  selection: UnmatchedDocument[];
  onSelection: React.Dispatch<React.SetStateAction<UnmatchedDocument[]>>;
  jobStatus: string;
  documentImporterConfiguration: DocumentImporterConfiguration;
};

const PayslipIssuesTable: React.FC<PayslipIssuesTableProps> = ({
  data,
  selection,
  onSelection,
  jobStatus,
  documentImporterConfiguration,
}) => {
  const [search, setSearch] = useState("");
  const [opened, setOpened] = useState(false);
  const [elementId, setElementId] = useState(0);
  const [jobId, setJobId] = useState(0);
  const [headers, setHeaders] = useState<string[]>([]);
  const auth = useContext(AuthContext);
  const analytics = useContext(AnalyticsContext);
  const { trackDocumentsEvent } = analytics || {};

  const {
    isLoading,
    isError,
    error,
    data: employeeData,
  } = useEmployeeListSearchApiService({
    employer: auth?.employer?.id ?? 0,
    pagination: false,
    search,
    job_id: jobId,
    unmatched: true,
    employee_limit: 10,
  });

  useEffect(() => {
    if (
      ![
        DocumentProcessingJobStatus.MATCHED,
        DocumentProcessingJobStatus.PARTIALLY_MATCHED,
        DocumentProcessingJobStatus.FINALIZED,
        DocumentProcessingJobStatus.MATCHES_NEEDED,
      ].includes(jobStatus)
    ) {
      setHeaders(["Pages", "Issue Date", "Distribution Date", "Error"]);
    } else {
      setHeaders(["Page", "Issue Date", "Distribution Date", "Error", ""]);
    }
  }, [jobStatus]);

  if (!auth) {
    return <Navigate replace to="/login" />;
  }

  function getPageNumberString(
    start: number | undefined,
    end?: number
  ): string {
    if (start !== undefined && end !== undefined && start !== end) {
      return `${start + 1} - ${end + 1}`;
    }
    if (start !== undefined) {
      return (start + 1).toString();
    }
    return "";
  }

  return (
    <>
      <DirectoryTable<UnmatchedDocument>
        data={data.documents}
        hideSelectControls
        selection={selection}
        onSelect={onSelection}
        tableVerticalSpacing="sm"
        tableHorizontalSpacing="sm"
        headers={headers}
        headersStyles={{ Error: { width: "300px" } }}
        rows={data.documents.map((it) => ({
          pages: `${getPageNumberString(
            it.page_number_start,
            it.page_number_end
          )}`,
          issueDate: dateFormat(data.date_issued),
          distributionDate: dateFormat(data.date_of_distribution),
          error: it.status_reason || "-",
          action: [
            DocumentProcessingJobStatus.MATCHED,
            DocumentProcessingJobStatus.PARTIALLY_MATCHED,
            DocumentProcessingJobStatus.FINALIZED,
            DocumentProcessingJobStatus.MATCHES_NEEDED,
          ].includes(jobStatus) ? (
            <Button
              id="match-payslips-btn"
              mr={4}
              variant="outline"
              size="sm"
              color="jem-theme"
              disabled={
                ![
                  DocumentProcessingJobStatus.MATCHED,
                  DocumentProcessingJobStatus.PARTIALLY_MATCHED,
                  DocumentProcessingJobStatus.FINALIZED,
                  DocumentProcessingJobStatus.MATCHES_NEEDED,
                ].includes(jobStatus)
              }
              onClick={() => {
                setElementId(it.id);
                setJobId(data.id);
                setOpened(true);
                trackDocumentsEvent({
                  event: `Match_${documentImporterConfiguration.document_name_plural}_Dashboard_Match_CTA`,
                  description: `User clicks on the Assign CTA on the unmatched ${documentImporterConfiguration.document_name_plural_lower_case} dashboard`,
                  event_type: "CTA:Click",
                  job: data,
                  type: documentImporterConfiguration.document_name_plural,
                });
              }}
              loading={isLoading}
              style={{
                color: "var(--primary-primary-base)",
                borderColor: "var(--primary-primary-base)",
                borderWidth: "1px",
                fontSize: "14px",
                fontWeight: 500,
              }}
              loaderPosition="right"
            >
              Assign {documentImporterConfiguration.document_name}
            </Button>
          ) : null,
        }))}
      />
      <MatchPayslipModal
        id={elementId}
        jobId={jobId}
        employeeList={employeeData}
        setSearch={setSearch}
        setOpened={setOpened}
        opened={opened}
        isError={isError}
        error={error}
        data={data}
        documentImporterConfiguration={documentImporterConfiguration}
      />
    </>
  );
};

export default PayslipIssuesTable;
